export const SESSION_END_REASON_PARAM = 'reason';
export const SIGNED_IN_ELSEWHERE = 'signed_in_elsewhere';

export const SIGNED_IN_ELSEWHERE_MESSAGE =
  'You were signed out because your account was signed in on another device.';

const REVOKED_BY_NEWER_LOGIN = 'Revoked by Newer Login';

type AuthFailure = { code?: string | null; message?: string | null };

export const isRevokedByNewerLogin = (
  failure: AuthFailure | null | undefined
) =>
  failure?.code === 'session_expired' &&
  (failure.message ?? '').includes(REVOKED_BY_NEWER_LOGIN);

export const sessionEndMessageFor = (reason: string | null) =>
  reason === SIGNED_IN_ELSEWHERE ? SIGNED_IN_ELSEWHERE_MESSAGE : null;

export const SIGNED_IN_ELSEWHERE_SIGN_IN_PATH = `/authentication/sign-in?${new URLSearchParams(
  { [SESSION_END_REASON_PARAM]: SIGNED_IN_ELSEWHERE }
)}`;

const sessionEvents = new EventTarget();
const REVOKED_BY_NEWER_LOGIN_EVENT = 'revoked-by-newer-login';

export function onRevokedByNewerLogin(listener: () => void): () => void {
  sessionEvents.addEventListener(REVOKED_BY_NEWER_LOGIN_EVENT, listener);
  return () =>
    sessionEvents.removeEventListener(REVOKED_BY_NEWER_LOGIN_EVENT, listener);
}

const REVOKED_AT_STORAGE_KEY = 'titanx:auth:revoked-by-newer-login-at';
const RECENT_REVOCATION_MS = 60_000;

function recordRevocationForOtherTabs(): boolean {
  try {
    localStorage.setItem(REVOKED_AT_STORAGE_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

export function wasRecentlyRevokedByNewerLogin(): boolean {
  try {
    const revokedAt = Number(localStorage.getItem(REVOKED_AT_STORAGE_KEY));
    return Date.now() - revokedAt < RECENT_REVOCATION_MS;
  } catch {
    return false;
  }
}

const requestUrl = (input: Parameters<typeof fetch>[0]) =>
  input instanceof Request ? input.url : String(input);

function isRefreshTokenRequest(url: string): boolean {
  const { pathname, searchParams } = new URL(url);
  return (
    pathname.endsWith('/auth/v1/token') &&
    searchParams.get('grant_type') === 'refresh_token'
  );
}

async function authFailureOf(response: Response): Promise<AuthFailure | null> {
  const body = await response
    .clone()
    .json()
    .catch(() => null);
  return body
    ? {
        code: body.error_code ?? body.code,
        message: body.msg ?? body.message,
      }
    : null;
}

export function withNewerLoginDetection(
  fetchImpl: typeof fetch = (...args) => fetch(...args)
): typeof fetch {
  return async (input, init) => {
    const response = await fetchImpl(input, init);
    if (
      !response.ok &&
      isRefreshTokenRequest(requestUrl(input)) &&
      isRevokedByNewerLogin(await authFailureOf(response))
    ) {
      recordRevocationForOtherTabs();
      sessionEvents.dispatchEvent(new Event(REVOKED_BY_NEWER_LOGIN_EVENT));
    }
    return response;
  };
}
